// S15 — Dashboard page (Agent M, Sprint 15).
//
// Real BE-backed analytics dashboard, replacing the prior speculative skeleton.
//
// Composition:
//   1. KPI tile row    — from /admin/dashboard/summary  (DashboardSummaryDto.tiles)
//   2. Sparkline chart — from /admin/dashboard/summary  (sevenDayTrend)
//   3. Metric drill-in chart — picker on tiles, fetches
//      /admin/dashboard/metrics/{key}/series for the last 30 days
//   4. Recent activity timeline — last 10 audit log entries
//   5. Quick links — content creation buttons
//
// Responsive grid: 4 KPI tiles on xl, 2 on md, 1 on xs.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Grid,
  Row,
  Segmented,
  Skeleton,
  Space,
  Statistic,
  Tooltip as AntTooltip,
  Typography,
} from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  HelpCircle,
  Plus,
  FolderOpen,
  RefreshCw,
  TrendingUp,
} from 'lucide-react';
import dayjs from 'dayjs';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PageHeader } from '@shared/components/PageHeader';
import { Timeline, type TimelineItem } from '@shared/components/Timeline';
import { ROUTES } from '@shared/constants/routes';
import { useAuth } from '@features/auth/hooks/useAuth';
import { dashboardApi, type DashboardSummary } from '@api/dashboard';
import { auditLogApi } from '@api/auditLog';
import { resolveApiErrorMessage } from '@/i18n/apiError';

const { Text } = Typography;

// Resolver for the human-readable Turkish labels of the metric keys the BE
// emits. The keys live under the `dashboard:metric.*` namespace and MUST mirror
// `DashboardMetricKeys` on the backend (dotted keys); any drift makes the tile
// render a humanised form of its raw key (e.g. "users total") to the user.
type MetricLabeler = (key: string) => string;

function makeLabelForMetric(td: (key: string) => string): MetricLabeler {
  return (key: string): string => {
    const mapped = td(`metric.${key}`);
    // i18next returns the key itself when missing → fall back to a humanised
    // form (dots/underscores → spaces) so an unmapped key stays readable.
    return mapped === `metric.${key}` ? key.replace(/[._]/g, ' ') : mapped;
  };
}

function formatNumber(n: number): string {
  return Number.isFinite(n) ? n.toLocaleString('tr-TR') : '0';
}

function describeDelta(deltaPercent: number): {
  sign: '+' | '-' | '';
  abs: string;
  tone: 'success' | 'danger' | 'neutral';
} {
  // The BE already expresses `deltaPercent` in percentage points
  // (`((current - previous) / previous) * 100`, rounded to 2dp), so we format
  // it as-is — multiplying by 100 again would turn a +12% change into "1.200%".
  if (!Number.isFinite(deltaPercent) || deltaPercent === 0) {
    return { sign: '', abs: '0', tone: 'neutral' };
  }
  const sign = deltaPercent > 0 ? '+' : '-';
  const tone = deltaPercent > 0 ? 'success' : 'danger';
  const abs = Math.abs(deltaPercent).toLocaleString('tr-TR', {
    maximumFractionDigits: 1,
  });
  return { sign, abs, tone };
}

function toneColor(tone: 'success' | 'danger' | 'neutral'): string {
  if (tone === 'success') return 'var(--ds-success, #16a34a)';
  if (tone === 'danger') return 'var(--ds-danger, #dc2626)';
  return 'var(--ds-text-tertiary, #94a3b8)';
}

interface KpiTileProps {
  metricKey: string;
  value: number;
  previousValue: number;
  deltaPercent: number;
  selected: boolean;
  onSelect: () => void;
  labelForMetric: MetricLabeler;
  td: (key: string, options?: Record<string, unknown>) => string;
}

function KpiTile({
  metricKey,
  value,
  previousValue,
  deltaPercent,
  selected,
  onSelect,
  labelForMetric,
  td,
}: KpiTileProps) {
  const delta = describeDelta(deltaPercent);
  const DeltaIcon =
    delta.tone === 'success' ? ArrowUpRight : delta.tone === 'danger' ? ArrowDownRight : TrendingUp;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      aria-label={td('tile.selectAriaLabel', { label: labelForMetric(metricKey) })}
      data-testid={`dashboard-tile-${metricKey}`}
      style={{
        width: '100%',
        padding: 0,
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <Card
        size="small"
        styles={{ body: { padding: 16 } }}
        style={{
          height: '100%',
          borderColor: selected ? 'var(--ds-brand, #2563eb)' : undefined,
          boxShadow: selected
            ? '0 0 0 2px var(--ds-brand-soft, rgba(37,99,235,0.15))'
            : undefined,
          transition: 'box-shadow 120ms, border-color 120ms',
        }}
      >
        <Statistic
          title={labelForMetric(metricKey)}
          value={formatNumber(value)}
          valueStyle={{ fontWeight: 700, fontSize: 22 }}
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            marginTop: 6,
            fontSize: 12,
            color: toneColor(delta.tone),
          }}
        >
          <DeltaIcon size={14} />
          <span>
            {delta.sign}
            {delta.abs}%
          </span>
          <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>
            {td('tile.previousDay', { value: formatNumber(previousValue) })}
          </Text>
        </div>
      </Card>
    </button>
  );
}

// Group the 7-day trend points into one ascending-by-date series per metric.
function groupTrendByMetric(
  summary: DashboardSummary,
): Array<{ metricKey: string; points: Array<{ value: number }>; delta: number }> {
  const byKey = new Map<string, Array<{ date: string; value: number }>>();
  for (const p of summary.sevenDayTrend) {
    const arr = byKey.get(p.metricKey) ?? [];
    arr.push({ date: p.date, value: p.value });
    byKey.set(p.metricKey, arr);
  }
  // Preserve the BE tile order so the right rail mirrors the KPI row above.
  const order = summary.tiles.map((t) => t.metricKey);
  const keys = order.length ? order : [...byKey.keys()];
  const deltaByKey = new Map(summary.tiles.map((t) => [t.metricKey, t.deltaPercent]));
  return keys
    .filter((k) => byKey.has(k))
    .map((metricKey) => ({
      metricKey,
      points: (byKey.get(metricKey) ?? [])
        .sort((a, b) => a.date.localeCompare(b.date))
        .map((p) => ({ value: p.value })),
      delta: deltaByKey.get(metricKey) ?? 0,
    }));
}

interface MetricTrendRowProps {
  metricKey: string;
  points: Array<{ value: number }>;
  delta: number;
  selected: boolean;
  onSelect: () => void;
  labelForMetric: MetricLabeler;
  td: (key: string, options?: Record<string, unknown>) => string;
}

// One compact row: label + mini sparkline + delta chip. The "small multiples"
// layout lets the user compare the SHAPE of every metric's trend at a glance —
// information the single big chart (one metric) and the KPI tiles (one number)
// don't convey — and clicking drives the detail chart.
function MetricTrendRow({
  metricKey,
  points,
  delta,
  selected,
  onSelect,
  labelForMetric,
  td,
}: MetricTrendRowProps) {
  const tone = describeDelta(delta);
  const color = toneColor(tone.tone);
  const stroke = selected ? 'var(--ds-brand, #2563eb)' : 'var(--ds-text-tertiary, #94a3b8)';
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      aria-label={td('tile.trendAriaLabel', { label: labelForMetric(metricKey) })}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 72px auto',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '8px 10px',
        border: 'none',
        borderRadius: 8,
        cursor: 'pointer',
        textAlign: 'left',
        background: selected ? 'var(--ds-brand-soft, rgba(37,99,235,0.08))' : 'transparent',
        transition: 'background 120ms',
      }}
    >
      <Text style={{ fontSize: 12, fontWeight: selected ? 600 : 400 }} ellipsis>
        {labelForMetric(metricKey)}
      </Text>
      <ResponsiveContainer width={72} height={28}>
        <AreaChart data={points} margin={{ top: 2, right: 0, bottom: 2, left: 0 }}>
          <Area
            type="monotone"
            dataKey="value"
            stroke={stroke}
            strokeWidth={1.5}
            fill="none"
            dot={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
      <span style={{ fontSize: 11, color, whiteSpace: 'nowrap', minWidth: 44, textAlign: 'right' }}>
        {tone.sign}
        {tone.abs}%
      </span>
    </button>
  );
}

type SeriesWindow = 7 | 14 | 30;

export function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const { t } = useTranslation(['dashboard', 'common', 'errors']);
  const td = t as unknown as (key: string, options?: Record<string, unknown>) => string;
  const labelForMetric = useMemo(() => makeLabelForMetric(td), [td]);

  const [selectedMetric, setSelectedMetric] = useState<string>('');
  const [seriesWindow, setSeriesWindow] = useState<SeriesWindow>(7);

  const summaryQ = useQuery({
    queryKey: ['dashboard', 'summary'],
    queryFn: () => dashboardApi.summary(),
    staleTime: 60_000,
  });

  // Default to the first tile once the summary lands.
  const summary = summaryQ.data;
  const effectiveMetric =
    selectedMetric || summary?.tiles[0]?.metricKey || 'articles';

  const seriesQ = useQuery({
    queryKey: ['dashboard', 'series', effectiveMetric, seriesWindow],
    queryFn: () =>
      dashboardApi.series(
        effectiveMetric,
        dayjs().subtract(seriesWindow - 1, 'day').format('YYYY-MM-DD'),
        dayjs().format('YYYY-MM-DD'),
      ),
    enabled: !!effectiveMetric,
    staleTime: 60_000,
  });

  const auditQ = useQuery({
    queryKey: ['dashboard', 'recent-audit'],
    queryFn: () => auditLogApi.list({ pageSize: 10 }),
    staleTime: 30_000,
  });

  const seriesData = useMemo(
    () =>
      (seriesQ.data ?? []).map((p) => ({
        date: dayjs(p.date).format('DD MMM'),
        value: p.value,
      })),
    [seriesQ.data],
  );

  const metricTrends = useMemo(
    () => (summary ? groupTrendByMetric(summary) : []),
    [summary],
  );

  const timelineItems: TimelineItem[] = useMemo(
    () =>
      (auditQ.data?.items ?? []).map((entry) => ({
        id: entry.id,
        time: dayjs(entry.at).format('DD MMM HH:mm'),
        actor: entry.actorName,
        action: entry.summary,
        tone:
          entry.action.includes('delete') || entry.action.includes('reject')
            ? 'danger'
            : entry.action.includes('publish') || entry.action.includes('approve')
              ? 'success'
              : entry.action.includes('archive') || entry.action.includes('rotate')
                ? 'warning'
                : 'info',
      })),
    [auditQ.data?.items],
  );

  const firstName = user?.fullName?.split(' ')[0];
  const greetingTitle = firstName
    ? td('page.greeting', { name: firstName })
    : td('page.greetingFallback');

  // Manual refresh re-fetches every panel together; `isFetching` drives the
  // spinner so the tester gets clear feedback that data is reloading.
  const isRefreshing =
    summaryQ.isFetching || seriesQ.isFetching || auditQ.isFetching;
  const refreshAll = () => {
    void summaryQ.refetch();
    void seriesQ.refetch();
    void auditQ.refetch();
  };
  const lastUpdated = summaryQ.dataUpdatedAt
    ? dayjs(summaryQ.dataUpdatedAt).format('HH:mm')
    : null;

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title={greetingTitle}
        description={td('page.description')}
        breadcrumb={[{ label: td('breadcrumb.title') }]}
        actions={
          <Space size={8} wrap>
            {lastUpdated ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {td('header.lastUpdated', { time: lastUpdated })}
              </Text>
            ) : null}
            <AntTooltip title={td('header.refreshTooltip')}>
              <Button
                icon={<RefreshCw size={14} />}
                loading={isRefreshing}
                onClick={refreshAll}
              >
                {td('header.refresh')}
              </Button>
            </AntTooltip>
          </Space>
        }
      />

      {summaryQ.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={td('summary.error')}
          description={
            summaryQ.error
              ? resolveApiErrorMessage(summaryQ.error)
              : td('summary.errorDescription')
          }
          action={
            <Button size="small" danger onClick={() => void summaryQ.refetch()}>
              {td('summary.retry')}
            </Button>
          }
        />
      ) : null}

      {/* KPI tiles */}
      <Row gutter={[12, 12]} data-testid="dashboard-tiles">
        {summaryQ.isLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <Col xs={24} sm={12} lg={8} key={i}>
              <Card size="small">
                <Skeleton active paragraph={{ rows: 2 }} />
              </Card>
            </Col>
          ))
        ) : (summary?.tiles ?? []).length === 0 ? (
          <Col xs={24}>
            <Card size="small">
              <Empty description={td('summary.emptyKpi')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </Card>
          </Col>
        ) : (
          (summary?.tiles ?? []).map((tile) => (
            <Col xs={24} sm={12} lg={8} key={tile.metricKey}>
              <KpiTile
                metricKey={tile.metricKey}
                value={tile.value}
                previousValue={tile.previousValue}
                deltaPercent={tile.deltaPercent}
                selected={effectiveMetric === tile.metricKey}
                onSelect={() => setSelectedMetric(tile.metricKey)}
                labelForMetric={labelForMetric}
                td={td}
              />
            </Col>
          ))
        )}
      </Row>

      {/* Series chart + sparkline */}
      <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={16}>
          <Card
            title={
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <TrendingUp size={16} />{' '}
                {td('series.cardTitle', {
                  label: labelForMetric(effectiveMetric),
                  count: seriesWindow,
                })}
              </span>
            }
            extra={
              <Segmented
                size="small"
                value={seriesWindow}
                onChange={(v) => setSeriesWindow(Number(v) as SeriesWindow)}
                options={[
                  { label: td('series.windowOptions.d7'), value: 7 },
                  { label: td('series.windowOptions.d14'), value: 14 },
                  { label: td('series.windowOptions.d30'), value: 30 },
                ]}
              />
            }
          >
            {seriesQ.isLoading ? (
              <Skeleton active paragraph={{ rows: 6 }} />
            ) : seriesQ.isError ? (
              <Alert
                type="error"
                showIcon
                message={td('series.error')}
                action={
                  <Button size="small" onClick={() => void seriesQ.refetch()}>
                    {td('series.retry')}
                  </Button>
                }
              />
            ) : seriesData.length === 0 ? (
              <Empty description={td('series.empty')} />
            ) : (
              <ResponsiveContainer width="100%" height={isMobile ? 220 : 280}>
                <LineChart data={seriesData} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--ds-border-subtle, #eef2f7)" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip
                    formatter={(v: number) => [
                      formatNumber(v),
                      labelForMetric(effectiveMetric),
                    ]}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#2563eb"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card
            styles={{ body: { padding: 8 } }}
            title={
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <TrendingUp size={16} /> {td('trends.cardTitle')}
              </span>
            }
          >
            {summaryQ.isLoading ? (
              <div style={{ padding: 8 }}>
                <Skeleton active paragraph={{ rows: 5 }} />
              </div>
            ) : metricTrends.length > 0 ? (
              <Space direction="vertical" size={2} style={{ width: '100%' }}>
                {metricTrends.map((m) => (
                  <MetricTrendRow
                    key={m.metricKey}
                    metricKey={m.metricKey}
                    points={m.points}
                    delta={m.delta}
                    selected={effectiveMetric === m.metricKey}
                    onSelect={() => setSelectedMetric(m.metricKey)}
                    labelForMetric={labelForMetric}
                    td={td}
                  />
                ))}
              </Space>
            ) : (
              <Empty description={td('trends.empty')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>
        </Col>
      </Row>

      {/* Recent activity + Quick links */}
      <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={16}>
          <Card
            title={td('activity.cardTitle')}
            data-testid="dashboard-recent-activity"
            extra={
              <Button
                type="link"
                size="small"
                style={{ paddingInline: 0 }}
                onClick={() => void navigate(ROUTES.AUDIT_LOG)}
              >
                {td('activity.viewAll')} <ArrowRight size={14} style={{ marginLeft: 2 }} />
              </Button>
            }
          >
            {auditQ.isLoading ? (
              <Skeleton active paragraph={{ rows: 6 }} />
            ) : auditQ.isError ? (
              <Alert
                type="error"
                showIcon
                message={td('activity.error')}
                action={
                  <Button size="small" onClick={() => void auditQ.refetch()}>
                    {td('activity.retry')}
                  </Button>
                }
              />
            ) : timelineItems.length > 0 ? (
              <Timeline items={timelineItems} />
            ) : (
              <Empty description={td('activity.empty')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title={td('quickLinks.cardTitle')} data-testid="dashboard-quick-links">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                block
                type="primary"
                icon={<Plus size={14} />}
                onClick={() => void navigate(ROUTES.ARTICLE_NEW)}
              >
                {td('quickLinks.newArticle')}
              </Button>
              <Button
                block
                icon={<HelpCircle size={14} />}
                onClick={() => void navigate(ROUTES.SSS_NEW)}
              >
                {td('quickLinks.newFaq')}
              </Button>
              <Button
                block
                icon={<FolderOpen size={14} />}
                onClick={() => void navigate(ROUTES.MEDIA)}
              >
                {td('quickLinks.media')}
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default DashboardPage;
